<template>
    <div class="px-4 sm:px-6">
        <div
            class="-mx-2 col-span-3 flex items-center md:grid md:grid-cols-3 md:gap-x-4"
        >
            <div class="mx-auto text-center">
                <label class="block text-sm font-bold text-gray-700">
                    Quantity
                </label>

                <div
                    class="shrink-0 mt-2 relative z-0 inline-flex shadow-sm rounded-md"
                >
                    <button
                        id="decrease-quantity"
                        @click="decreaseQuantity"
                        type="button"
                        :class="[
                            state.quantity > 1
                                ? 'bg-white hover:bg-gray-50 text-gray-500'
                                : 'bg-gray-100 text-gray-300 cursor-not-allowed',
                            'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium focus:z-10 focus:outline-none',
                        ]"
                    >
                        <span class="sr-only">-1</span>
                        <MinusIcon class="h-5 w-5" aria-hidden="true" />
                    </button>

                    <Listbox class="-ml-px" as="div" v-model="selectedQuantity">
                        <ListboxLabel />
                        <div class="relative">
                            <ListboxButton
                                class="bg-white relative w-full border border-gray-300 px-6 py-2 text-left cursor-default focus:outline-none font-semibold text-sm text-gray-700"
                            >
                                <span class="block">{{
                                    selectedQuantity
                                }}</span>
                            </ListboxButton>

                            <transition
                                leave-active-class="transition ease-in duration-100"
                                leave-from-class="opacity-100"
                                leave-to-class="opacity-0"
                            >
                                <ListboxOptions
                                    class="absolute z-20 mt-1 w-full bg-white border border-gray-300 shadow-lg max-h-36 rounded-md py-1 overflow-y-auto overflow-x-hidden focus:outline-none text-sm"
                                >
                                    <ListboxOption
                                        as="template"
                                        v-for="quantityOption in recommendedProduct
                                            .default_product_option
                                            .maximum_quantity"
                                        :key="quantityOption"
                                        :value="quantityOption.toString()"
                                        v-slot="{ active, selected }"
                                    >
                                        <li
                                            :class="[
                                                active
                                                    ? 'text-white bg-nutreance-green'
                                                    : 'text-gray-700',
                                                'cursor-default select-none py-2 px-2 flex justify-between items-center space-x-1',
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    selected
                                                        ? 'font-semibold'
                                                        : 'font-normal',
                                                ]"
                                            >
                                                {{ quantityOption }}
                                            </span>
                                            <span
                                                v-if="selected"
                                                :class="[
                                                    active
                                                        ? 'text-white'
                                                        : 'text-nutreance-green',
                                                ]"
                                            >
                                                <CheckIcon
                                                    class="h-5 w-5"
                                                    aria-hidden="true"
                                                />
                                            </span>
                                        </li>
                                    </ListboxOption>
                                </ListboxOptions>
                            </transition>
                        </div>
                    </Listbox>

                    <button
                        id="increase-quantity"
                        @click="increaseQuantity"
                        type="button"
                        class="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none"
                    >
                        <span class="sr-only">+1</span>
                        <PlusIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>

                <p
                    class="px-3 py-1 text-sm text-red-600"
                    v-if="errors.quantity"
                >
                    {{ errors.quantity }}
                </p>
            </div>

            <div class="mx-auto text-center">
                <label class="block text-sm font-bold text-gray-700">
                    Size
                </label>
                <div class="mt-2 text-gray-700">
                    {{ recommendedProduct.default_product_option.description }}
                </div>
            </div>

            <div class="mx-auto text-center">
                <label class="block text-sm font-bold text-gray-700">
                    Price
                </label>
                <div class="mt-2 text-gray-700">
                    ${{
                        dollarsForDisplay(
                            recommendedProduct.default_product_option.price,
                        )
                    }}
                </div>
            </div>
        </div>

        <button
            @click="addToCart"
            v-if="!added && state.discounts && state.discounts.length > 0"
            type="button"
            class="text-sm mt-3 w-full p-4 focus:outline-none bg-emerald-50 border border-emerald-100 rounded-lg"
        >
            <span
                class="block ml-3 text-left text-nutreance-green hover:text-nutreance-green-darker"
            >
                Click "Add to Cart" now to get:
            </span>
            <span
                v-for="discount in state.discounts"
                :key="discount.id"
                class="mt-2 ml-3 text-left font-semibold text-nutreance-green hover:text-nutreance-green-darker flex items-center"
            >
                <CheckIcon
                    class="h-5 w-5 mr-2 text-nutreance-green shrink-0"
                    aria-hidden="true"
                />
                {{ discount.name }}
            </span>
        </button>

        <button
            @click="addToCart"
            name="add_recommended_product_to_cart"
            type="button"
            class="mt-3 w-full py-3 shadow rounded-md text-base font-medium text-white bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none"
        >
            <span v-if="!added" class="flex items-center justify-center">
                <LoadingIcon v-if="loading" class="h-5 w-5 mr-2 text-white" />
                <ShoppingCartIcon
                    v-else
                    class="h-5 w-5 mr-2"
                    aria-hidden="true"
                />
                Add {{ recommendedProduct.name }} to Cart
            </span>
            <span v-if="added" class="flex items-center justify-center">
                <CheckCircleIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                Added {{ quantityAdded }}
                {{ quantityAdded > 1 ? "bottles" : "bottle" }} to cart!
            </span>
        </button>
        <PhoneNumber class="text-center mb-2" :headquarters="headquarters" />
    </div>
</template>

<script setup lang="ts">
import { inject, onMounted, reactive, ref, watch } from "vue";
import {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
} from "@headlessui/vue";
import {
    CheckIcon,
    CheckCircleIcon,
    MinusIcon,
    PlusIcon,
    ShoppingCartIcon,
} from "@heroicons/vue/solid";
import { useMoney } from "../../functions/useMoney";
import PhoneNumber from "./PhoneNumber.vue";
import LoadingIcon from "../LoadingIcon.vue";
import { useGoogleDataLayer } from "../../functions/useGoogleDataLayer";
import { addCartItem } from "../../api/cart";
import { getProductOptions } from "../../api/products";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    headquarters?: any;
    product?: any;
    recommendedProduct?: any;
    subscriptionOptionId?: number | null;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const { dollarsForDisplay } = useMoney();
const { pushAddToCartToDataLayer } = useGoogleDataLayer();
const loading = ref(false);
const added = ref(false);
const quantityAdded = ref(0);
const errors = reactive<Record<string, any>>({});
const state = reactive<Record<string, any>>({
    discounts: null,
    subscribe: !!props.subscriptionOptionId,
    productOptionId: props.recommendedProduct.default_product_option.id,
    subscriptionOptionId: props.subscriptionOptionId,
    quantity: 1,
});
const selectedQuantity = ref(state.quantity.toString());

watch(
    () => selectedQuantity.value,
    (quantity) => {
        updateQuantity(parseInt(quantity));
    },
);

watch(
    () => state.quantity,
    (quantity) => {
        selectedQuantity.value = quantity.toString();
    },
);

updateDiscounts();

function decreaseQuantity() {
    if (state.quantity === 1) {
        return;
    }

    updateQuantity(state.quantity - 1);
}

function increaseQuantity() {
    if (
        state.quantity + 1 >
        props.recommendedProduct.default_product_option.maximum_quantity
    ) {
        return (errors.quantity = `The maximum quantity allowed is ${props.recommendedProduct.default_product_option.maximum_quantity}`);
    }

    updateQuantity(state.quantity + 1);
}

function updateQuantity(quantity: number) {
    state.quantity = quantity;
    clearErrors();
    updateDiscounts();
}

function addToCart() {
    if (loading.value) {
        return;
    }

    if (added.value) {
        added.value = false;
        quantityAdded.value = 0;
        return;
    }

    const params: Record<string, any> = {
        product_option_id: state.productOptionId,
        quantity: state.quantity,
    };

    if (state.subscriptionOptionId) {
        params.subscription_option_id = state.subscriptionOptionId;
    }

    loading.value = true;

    addCartItem(params)
        .then((data) => {
            emitter.emit("cartItemUpdated", data);
            clearErrors();

            added.value = true;
            quantityAdded.value = state.quantity;
            pushAddToCartToDataLayer({
                product: props.recommendedProduct,
                subscribe: state.subscribe,
                quantity: state.quantity,
                source: `${props.product.name} Recommended Product Side Bar for ${props.recommendedProduct.name}`,
            });

            window.location.href = "/cart";
        })
        .catch((error: any) => {
            if (
                typeof error.response !== "undefined" &&
                Object.prototype.hasOwnProperty.call(error.response, "data") &&
                Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                const field = Object.keys(error.response.data.errors)[0];
                errors[field] = error.response.data.errors[field][0];
            }
        })
        .finally(() => (loading.value = false));
}

function clearErrors() {
    for (const field in errors) {
        delete errors[field];
    }
}

function updateDiscounts() {
    const params: Record<string, any> = {
        product_option_id: state.productOptionId,
        quantity: state.quantity,
    };

    if (state.subscriptionOptionId) {
        params.subscription_option_id = state.subscriptionOptionId;
    }

    getProductOptions(params)
        .then((data) => {
            state.discounts = data.discounts;
        })
        .catch(() => (state.discounts = {}));
}

onMounted(() => emitter.on("cartItemUpdated", () => updateDiscounts()));
</script>
